import React, { useEffect, useState } from 'react';
import { Divider, Tab, Tabs } from '@mui/material';
import DialogModal from '../../../../components/DialogModal';
import ConfirmationDialog from '../../../../components/ConfirmationDialog/ConfirmationDialog';

import pallet from '../../../../layouts/pallet';
import stringUtils from '../../../../utils/stringUtils';
import isEqual from 'lodash/isEqual';

import {
  newPaymentSchemeOptions,
  PaymentSchemeDetailTabs
} from '../../utils/constant';

import {
  InstallmentsProps,
  PaymentOption,
  FormFAttachmentsProps
} from '../../types';

import FormFTabContent from './FormFTabContent';
import InstallmentTabContent from './InstallmentTabContent';
import { StyledButton } from '../../styled';

import { useFeeStructureContext } from '../../context/FeeStructureContext';
import { PaymentSchemeType } from '../../../../enums';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';

const PaymentSchemeDetail = ({
  isPaymentSchemeDetail,
  onClose
}: {
  isPaymentSchemeDetail: {
    data: PaymentOption | null;
    isOpen: boolean;
  };
  onClose: () => void;
}) => {
  const {
    selectedAcademicYear,
    selectedGrade,
    admissionType,
    handlePaymentSchemeDetailUpdate
  } = useFeeStructureContext();

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const paymentSchemeData = isPaymentSchemeDetail.data;

  const [selectedTab, setSelectedTab] = useState<string>(
    PaymentSchemeType.INSTALLMENTS
  );
  const [installmentsData, setInstallmentsData] = useState<InstallmentsProps[]>(
    []
  );
  const [formFList, setFormFList] = useState<FormFAttachmentsProps[]>([]);
  const [originalInstallments, setOriginalInstallments] = useState<
    InstallmentsProps[]
  >([]);
  const [originalFormFList, setOriginalFormFList] = useState<
    FormFAttachmentsProps[]
  >([]);
  const [selectedRow, setSelectedRow] = useState<InstallmentsProps | null>(
    null
  );

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogMsg, setConfirmDialogMsg] = useState('');
  const [confirmDialogAction, setConfirmDialogAction] = useState<
    (() => void) | null
  >(null);
  const [pendingExit, setPendingExit] = useState(false);

  const selectedPaymentSchemeName = paymentSchemeData?.schemeName || '';
  const selectedSchemeData = newPaymentSchemeOptions.find(
    (scheme) =>
      scheme.label.toLowerCase() === selectedPaymentSchemeName.toLowerCase()
  );
  const feePeriodOptions =
    selectedSchemeData?.validFeePeriods.map((period) => ({
      label: period,
      value: period
    })) || [];
  const activeTab = PaymentSchemeDetailTabs.findIndex(
    (tab) => tab.id === selectedTab
  );

  useEffect(() => {
    setInstallmentsData(paymentSchemeData?.installments || []);
    setOriginalInstallments(paymentSchemeData?.installments || []);
    setFormFList(paymentSchemeData?.formFAttachments || []);
    setOriginalFormFList(paymentSchemeData?.formFAttachments || []);
  }, [paymentSchemeData?.installments, paymentSchemeData?.formFAttachments]);

  const isSaveDisabled =
    isEqual(installmentsData, originalInstallments) &&
    isEqual(formFList, originalFormFList);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const tabId = PaymentSchemeDetailTabs[newValue]?.id;
    if (tabId) {
      setSelectedTab(tabId);
    }
  };

  const handleAddNewInstallmentRow = () => {
    const newInstallmentRow = {
      id: new Date().getTime().toString(),
      particulars: '',
      feePeriod: '',
      amount: 0,
      paymentDueDate: ''
    };
    setInstallmentsData((prev = []) => {
      const newArr = [...prev, newInstallmentRow];
      setSelectedRow(newInstallmentRow);
      return newArr;
    });
  };

  const handleEditInstallmentRow = (selectedInstallment: InstallmentsProps) => {
    setSelectedRow(selectedInstallment);
  };

  const handleDeleteInstallmentRow = (
    selectedInstallment: InstallmentsProps
  ) => {
    setInstallmentsData((prev = []) => {
      const newArr = prev.filter(
        (installment) => installment.id !== selectedInstallment.id
      );
      if (selectedRow?.id === selectedInstallment.id) {
        setSelectedRow(null);
      }
      return newArr;
    });
  };

  const handleFieldChange = (
    selectedInstallment: InstallmentsProps,
    key: keyof InstallmentsProps,
    value: any
  ) => {
    setInstallmentsData((prev = []) => {
      let updated;
      if (key === 'paymentDueDate') {
        updated = prev.map((prevInstallment) =>
          prevInstallment.feePeriod === selectedInstallment.feePeriod
            ? { ...prevInstallment, paymentDueDate: value }
            : prevInstallment
        );
      } else {
        updated = prev.map((prevInstallment) => {
          if (prevInstallment.id && selectedInstallment.id) {
            return prevInstallment.id === selectedInstallment.id
              ? { ...prevInstallment, [key]: value }
              : prevInstallment;
          } else {
            return prevInstallment._id === selectedInstallment._id
              ? { ...prevInstallment, [key]: value }
              : prevInstallment;
          }
        });
      }
      return updated;
    });
  };

  const handleSave = () => {
    if (paymentSchemeData?._id) {
      handlePaymentSchemeDetailUpdate({
        newFormFList: formFList,
        newInstallmentsData: installmentsData,
        selectedPaymentSchemeId: paymentSchemeData?._id
      });
    }
    setOriginalFormFList(formFList);
    setOriginalInstallments(installmentsData);
    setSelectedRow(null);
  };

  const confirmDeleteInstallment = (selectedInstallment: InstallmentsProps) => {
    setConfirmDialogMsg('Are you sure you want to delete?');
    setConfirmDialogOpen(true);
    setConfirmDialogAction(() => () => {
      handleDeleteInstallmentRow(selectedInstallment);
      setConfirmDialogOpen(false);
    });
  };

  const confirmDeleteFormFItem = (idx: number) => {
    setConfirmDialogMsg('Are you sure you want to delete?');
    setConfirmDialogOpen(true);
    setConfirmDialogAction(() => () => {
      setFormFList((list) => list.filter((_, i) => i !== idx));
      setConfirmDialogOpen(false);
    });
  };

  const resetState = () => {
    onClose();
    setSelectedTab(PaymentSchemeType.INSTALLMENTS);
    setSelectedRow(null);
  };

  const requestClose = () => {
    if (!isSaveDisabled) {
      setConfirmDialogMsg(
        'There are some unsaved changes in the payment option. Are you sure you want to exit without saving?'
      );
      setConfirmDialogOpen(true);
      setConfirmDialogAction(() => () => {
        setConfirmDialogOpen(false);
        resetState();
      });
      setPendingExit(true);
    } else {
      resetState();
    }
  };

  const handleModalClose = () => requestClose();
  const handleDialogClose = () => {
    setConfirmDialogOpen(false);
    setPendingExit(false);
  };
  const handleDialogConfirm = () => {
    if (confirmDialogAction) confirmDialogAction();
    if (pendingExit) setPendingExit(false);
  };

  const finalTabs =
    loggedInUser?.school === 'BPIS'
      ? PaymentSchemeDetailTabs.filter(
          (tab) => tab.id !== PaymentSchemeType.FORMF
        )
      : PaymentSchemeDetailTabs;

  return (
    <DialogModal
      open={isPaymentSchemeDetail.isOpen}
      onClose={handleModalClose}
      title={`${selectedAcademicYear} ${stringUtils.formatStringToReadAbleText(
        admissionType
      )} Payment Scheme Master (Grade ${selectedGrade})`}
      maxWidth={'md'}
      actions={
        loggedInUser.school !== 'BPIS' && (
          <StyledButton disabled={isSaveDisabled} onClick={handleSave}>
            Save
          </StyledButton>
        )
      }
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label='icon position tabs example'
        sx={{
          minHeight: '38px'
        }}
      >
        {finalTabs.map((tab) => (
          <Tab
            key={tab.id}
            sx={{
              fontWeight: '500',
              color: pallet.formLabelText,
              textTransform: 'capitalize',
              padding: '8px 20px',
              fontSize: '15px',
              minHeight: '38px'
            }}
            iconPosition='start'
            label={tab.label}
          />
        ))}
      </Tabs>
      <Divider sx={{ mb: 3 }} />

      {selectedTab === PaymentSchemeType.INSTALLMENTS && (
        <InstallmentTabContent
          feePeriodOptions={feePeriodOptions}
          installmentsData={installmentsData}
          handleAddInstallmentRow={handleAddNewInstallmentRow}
          selectedRow={selectedRow}
          onEditRow={handleEditInstallmentRow}
          onDeleteRow={confirmDeleteInstallment}
          onFieldChange={handleFieldChange}
        />
      )}

      {selectedTab === PaymentSchemeType.FORMF && (
        <FormFTabContent
          formFList={formFList}
          setFormFList={setFormFList}
          schemeName={selectedPaymentSchemeName}
          onDeleteFormFItem={confirmDeleteFormFItem}
        />
      )}

      <ConfirmationDialog
        message={confirmDialogMsg}
        open={confirmDialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
      />
    </DialogModal>
  );
};

export default PaymentSchemeDetail;
